import React, { useState } from 'react';
import Modal, { ModalHeader, ModalTransition } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { AnalyticsTrackAction, AnalyticsTrackSubject, sendAnalyticsEvent } from '@atlassian/temp-analytics';
import { createInvoiceGroup } from '@atlassian/cssxp-proxies';
import { BillingProfileFormTrackerStepsV2, CreateBillingProfilePaneV2 } from '@css-xp/ppc-billing-profile-table';
import { CompanyNameInput } from '@css-xp/ppc-add-customer-modal';
import { useContactServicePayload } from '@css-xp/contact-resources';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { ShipToPartySidebar } from '@css-xp/ppc-ship-to-party-sidebar';
import { RequiredAsterisk } from '@atlaskit/form';
import { Text, Flex } from '@atlaskit/primitives';
import { CreateContactRelationshipsRequest } from '@css-xp/contact-resources/src/constant';
import { updateShipToParty, createContactRelationship } from '@atlassian/temp-proxies';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import { errorNotificationBuilder, NotificationTypes, useNotification } from '@css-xp/base';
import { BillingProfileResponse } from '../../../../types';
import { CreateCustomerAndBillingProfileHeader } from './create-customer-and-billing-profile-header';
import { getErrorMessage, ErrorStage } from './create-customer-and-billing-profile-modal';
import {
  StyledShipToContainer,
  StyledBillingProfileContainer,
  CreateCustomerStyledHeader,
  StyledCompanyName,
} from './create-customer-and-billing-profile-modal-v2.styles';

enum ModalScreen {
  COMPANY_NAME = 'company-name',
  CUSTOMER = 'customer',
  BILLING_PROFILE = 'billing-profile',
  FINALIZE = 'finalize',
}

export const CreateCustomerAndBillingProfileFormStages = {
  COMPANY_NAME: 0,
  ADD_ADDRESS: 1,
  BILLING_PROFILE: 2,
  FINALIZE: 3,
};

interface Props {
  isOpen: boolean;
  transactionAccountId: string;
  onClose: () => void;
  onSubmit: (_responsePayload?: {
    billingProfileInfo: BillingProfileResponse;
    customerName?: string | null | undefined;
    customerId?: string | undefined;
  }) => void;
  transactionAccountData?: any;
  transactionAccountDataLoading?: boolean;
  customerName?: string;
  isAddNewAddressFlow?: boolean;
  modalHeading?: string;
  isBPForMonthlyOrders?: boolean;
  isSoldToAddressArchivalEnabled?: boolean;
}

export const CreateCustomerAndBillingProfileModalV2 = ({
  isOpen,
  transactionAccountId,
  onClose,
  onSubmit,
  transactionAccountData,
  transactionAccountDataLoading,
  customerName = '',
  isAddNewAddressFlow = false,
  modalHeading,
  isBPForMonthlyOrders = false,
  isSoldToAddressArchivalEnabled,
}: Props) => {
  const [screen, setScreen] = useState(isAddNewAddressFlow ? ModalScreen.CUSTOMER : ModalScreen.COMPANY_NAME);
  const [formStage, setFormStage] = useState(0);
  const [companyName, setCompanyName] = React.useState(customerName);

  const [billingProfileFormData, setBillingProfileFormData] = useState<any>({});
  const [primaryCustomerContactAccountIds, setPrimaryCustomerContactAccountIds] = useState<string[]>([]);
  const [primaryCustomerContactAccountEmails, setPrimaryCustomerContactAccountEmails] = useState<string[]>(['']);
  const [secondaryCustomerContactAccountIds, setSecondaryCustomerContactAccountIds] = useState<string[]>([]);
  const [secondaryCustomerContactAccountEmails, setSecondaryCustomerContactAccountEmails] = useState<string[]>(['']);
  const [customerDetails, setCustomerDetails] = useState<CommerceAddress>({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [updateBillToPartyPayload, setUpdateBillToPartyPayload] = useState<any>(undefined);
  const { sendErrorNotification } = useNotification();
  const errorStage = React.useRef(ErrorStage.CUSTOMER_STEP);

  const BillingProfileFormStages = Object.values(BillingProfileFormTrackerStepsV2);

  const FormStages = isAddNewAddressFlow
    ? ['Add customer details', ...BillingProfileFormStages]
    : ['Create customer', 'Add customer details', ...BillingProfileFormStages];

  const { getCreateContactRelationshipsPayload } = useContactServicePayload();

  const onSubmitCustomerStep = (_customerDetails: CommerceAddress, updateBillToPartyPayload: any) => {
    if (!transactionAccountDataLoading) {
      setScreen(ModalScreen.BILLING_PROFILE);
      setCustomerDetails(_customerDetails);
      setUpdateBillToPartyPayload(updateBillToPartyPayload);
      setFormStage(formStage + 1);
    }
  };

  const onSubmitCreateBillingProfile = async (requestForm: any, contactServicePayload?: any) => {
    try {
      // create sold to address
      setIsSubmitLoading(true);
      //@ts-ignore
      const { id } = await updateShipToParty(updateBillToPartyPayload);
      if (id) {
        errorStage.current = ErrorStage.BILLING_PROFILE_STEP;
        //sending analytics event to track creation of new customer
        sendAnalyticsEvent(AnalyticsTrackAction.SUCCESSFUL, AnalyticsTrackSubject.NEW_CUSTOMER, {
          transactionAccountId,
        });
        const updatedRequestPayload = {
          data: { ...requestForm?.data, shipToPartyId: id },
          transactionAccountId: transactionAccountId,
        };
        const billingProfileResponse = await createInvoiceGroup(updatedRequestPayload);
        // create contact relationship
        if (billingProfileResponse?.id) {
          //on successfull creation of Billing Profile
          errorStage.current = ErrorStage.CONTACT_RELATIONSHIP_STEP;
          const createPayload = getCreateContactRelationshipsPayload({
            invoiceGroupId: billingProfileResponse?.id,
            ...contactServicePayload,
          });
          const contactServiceResponse = await createContactRelationship(
            createPayload as CreateContactRelationshipsRequest
          );
          if (contactServiceResponse) {
            setIsSubmitLoading(false);
          }
          onSubmit({
            customerName: customerDetails?.name,
            customerId: id,
            billingProfileInfo: billingProfileResponse,
          });
          onClose();
        }
      }
    } catch (err) {
      const notificationOptions = errorNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withHeader('Update unsuccessful')
        .withMessage(getErrorMessage(errorStage.current))
        .withDismissAfter(5000)
        .build();
      sendErrorNotification(notificationOptions);
      onClose();
    }
  };

  return (
    <ModalTransition>
      {isOpen && (
        <Modal onClose={onClose} testId="add-customer-modal" shouldCloseOnOverlayClick={false} height="600px">
          <>
            <ModalHeader>
              <Flex direction="column">
                <Text weight="bold" size="large">
                  {modalHeading ? modalHeading : `Create a customer`}
                </Text>
                <Text size="medium">
                  Required fields are marked with an asterisk <RequiredAsterisk />
                </Text>
              </Flex>
              <Button appearance="subtle" onClick={onClose} testId="add-customer-modal-close-button-with-icon">
                <CrossIcon size="small" label="Close Modal" primaryColor={token('color.text.subtle')} />
              </Button>
            </ModalHeader>
            <CreateCustomerStyledHeader>
              <CreateCustomerAndBillingProfileHeader formStage={formStage} isAddNewAddressFlow={isAddNewAddressFlow} />
            </CreateCustomerStyledHeader>
            {screen === ModalScreen.COMPANY_NAME && (
              <StyledCompanyName>
                <CompanyNameInput
                  transactionAccountId={transactionAccountId}
                  companyName={companyName}
                  formStages={FormStages}
                  currentStage={0}
                  onNext={(company: string) => {
                    setCompanyName(company);
                    setScreen(ModalScreen.CUSTOMER);
                    setFormStage(formStage + 1);
                  }}
                  showDefaultProgressIndicator={true}
                  isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
                />
              </StyledCompanyName>
            )}
            {screen === ModalScreen.CUSTOMER && (
              <StyledShipToContainer>
                <ShipToPartySidebar
                  transactionAccountId={transactionAccountId}
                  hideAddFromBillToAddress={true}
                  contentString={{
                    SUBMIT_BUTTON: 'Next',
                    NOTIFICATION_SUCCESS_HEADING: 'Customer added',
                  }}
                  FormStages={FormStages}
                  showPricingEligibility={true}
                  shouldHideHeader={true}
                  hideUpdateWarningMessage={true}
                  isProgressIndicatorEnabled={true}
                  shouldAllowMultipleAddresses={false}
                  onCancelClick={() => {
                    setScreen(ModalScreen.COMPANY_NAME);
                    setFormStage(formStage - 1);
                  }}
                  cancelButtonText="Back"
                  currentProgressIndex={isAddNewAddressFlow ? 0 : 1}
                  onSubmitCustomerStep={onSubmitCustomerStep}
                  setAddress={setCustomerDetails}
                  shipToAddress={{
                    ...customerDetails,
                    name: companyName,
                  }}
                  showDefaultProgressIndicator={true}
                  isAddType={isAddNewAddressFlow}
                  isCancelDisabled={isAddNewAddressFlow}
                  submitButtonAppearance={'default'}
                />
              </StyledShipToContainer>
            )}
            {screen === ModalScreen.BILLING_PROFILE && (
              <StyledBillingProfileContainer>
                <CreateBillingProfilePaneV2
                  transactionAccountId={transactionAccountId}
                  billingProfileFormData={billingProfileFormData}
                  transactionAccountDetails={transactionAccountData}
                  soldToAddresses={[customerDetails]}
                  isTermPaymentSelectionDisabled={isBPForMonthlyOrders}
                  closeModal={onClose}
                  onSubmit={onSubmitCreateBillingProfile}
                  defaultSoldToAddress={customerDetails}
                  isSubmitLoading={isSubmitLoading}
                  onBackCustomerClick={(billingProfileData, contactDetails) => {
                    setScreen(ModalScreen.CUSTOMER);
                    setFormStage(formStage - 1);
                    setBillingProfileFormData(billingProfileData);
                    setPrimaryCustomerContactAccountIds(contactDetails.primaryCustomerContactAccountIds);
                    setPrimaryCustomerContactAccountEmails(contactDetails.primaryCustomerContactAccountEmails);
                    setSecondaryCustomerContactAccountIds(contactDetails.secondaryCustomerContactAccountIds);
                    setSecondaryCustomerContactAccountEmails(contactDetails.secondaryCustomerContactAccountEmails);
                  }}
                  initialCurrentProgressIndex={isAddNewAddressFlow ? 1 : 2}
                  formStages={FormStages}
                  showMonthlyDefaultSelected={isBPForMonthlyOrders}
                  shouldHideFormHeader={true}
                  onNext={() => setFormStage(formStage + 1)}
                  onBack={() => setFormStage(formStage - 1)}
                  showDefaultProgressIndicator={true}
                  contentString={{
                    SOLD_TO_ADDRESS_LABEL: 'Sold to address',
                  }}
                  contactDetails={{
                    primaryCustomerContactAccountIds,
                    primaryCustomerContactAccountEmails,
                    secondaryCustomerContactAccountIds,
                    secondaryCustomerContactAccountEmails,
                  }}
                />
              </StyledBillingProfileContainer>
            )}
          </>
        </Modal>
      )}
    </ModalTransition>
  );
};
